import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useRoles } from '../hooks/useRoles.js';
import { Lock, Unlock, History } from 'lucide-react';
import WeekPicker from '../components/WeekPicker.jsx';
import ShiftModal from '../components/ShiftModal.jsx';
import RosterVersionPanel from '../components/RosterVersionPanel.jsx';

const DAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const FALLBACK_ROLES = ['kitchen', 'front_of_house', 'cashier', 'manager', 'bar', 'delivery'];

function getMondayOfCurrentWeek() {
  const d = new Date();
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'pm' : 'am';
  const h12 = hour % 12 || 12;
  return m === '00' ? `${h12}${ampm}` : `${h12}:${m}${ampm}`;
}

function roleLabel(r) {
  return r.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

const WARNING_COLORS = {
  UNFILLED_SLOT: 'danger',
  OVERTIME_WARNING: 'warning',
  BREAK_VIOLATION: 'danger',
  CONSECUTIVE_DAYS: 'warning',
  COMPLIANCE_SKIP: 'warning',
  MEAL_BREAK: 'info',
  PENALTY_RATE: 'info',
  WEATHER_ADJUSTMENT: 'info',
  EVENT_ADJUSTMENT: 'info',
  UNDER_HOURS: 'warning',
};

export default function RosterPage() {
  const api = useApi();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const { roleNames } = useRoles();
  const ROLES = roleNames.length > 0 ? roleNames : FALLBACK_ROLES;

  const [weekStart, setWeekStart] = useState(getMondayOfCurrentWeek());
  const [mobileDayIdx, setMobileDayIdx] = useState(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const monday = getMondayOfCurrentWeek();
    const dates = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
    const idx = dates.indexOf(todayStr);
    return idx >= 0 ? idx : 0;
  });
  const [roster, setRoster] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');

  // Modal state
  const [modal, setModal] = useState(null); // { shift, date, role } or null
  const [showVersions, setShowVersions] = useState(false);
  const [openShiftModal, setOpenShiftModal] = useState(null); // { shift } for staff request
  const [requesting, setRequesting] = useState(false);
  const [requestDone, setRequestDone] = useState([]); // shiftIds after success

  // Derived week dates (Mon=0 ... Sun=6)
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rosterData, empData] = await Promise.all([
        api.get(`/roster?week=${weekStart}`),
        isOwner ? api.get('/employees') : Promise.resolve([]),
      ]);
      setRoster(rosterData.roster);
      setShifts(rosterData.shifts || []);
      setEmployees(empData);
    } catch (err) {
      setError(err.message || 'Failed to load roster');
    } finally {
      setLoading(false);
    }
  }, [weekStart, isOwner]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleGenerate() {
    if (!window.confirm('Generate roster for this week? This will replace any existing draft shifts.')) return;
    setGenerating(true);
    setError('');
    setWarnings([]);
    try {
      const result = await api.post('/roster/generate', { week_start: weekStart });
      setRoster({ id: result.roster_id, week_start: weekStart, status: result.status, generated_at: new Date().toISOString() });
      setShifts(result.shifts || []);
      setWarnings(result.warnings || []);
    } catch (err) {
      setError(err.message || 'Failed to generate roster');
    } finally {
      setGenerating(false);
    }
  }

  async function handlePublish() {
    if (!roster) return;
    setPublishing(true);
    try {
      const updated = await api.put(`/roster/${roster.id}/publish`);
      setRoster(updated);
    } catch (err) {
      setError(err.message || 'Failed to publish roster');
    } finally {
      setPublishing(false);
    }
  }

  async function handleUnlock() {
    if (!roster) return;
    try {
      const updated = await api.put(`/roster/${roster.id}/unlock`);
      setRoster(updated);
    } catch (err) {
      setError(err.message || 'Failed to unlock roster');
    }
  }

  async function handleRequestOpenShift(shiftId) {
    setRequesting(true);
    try {
      await api.post(`/shifts/${shiftId}/request`, {});
      setRequestDone(prev => [...prev, shiftId]);
      setOpenShiftModal(null);
    } catch (err) {
      setError(err.message || 'Failed to request shift');
    } finally {
      setRequesting(false);
    }
  }

  function handleShiftSaved(saved) {
    setShifts(prev => {
      const idx = prev.findIndex(s => s.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [...prev, saved];
    });
    setModal(null);
  }

  function handleShiftDeleted(id) {
    setShifts(prev => prev.filter(s => s.id !== id));
    setModal(null);
  }

  // Get shifts for a specific date + role
  function getShiftsFor(date, role) {
    return shifts.filter(s => s.date === date && s.role === role);
  }

  // Get roles that actually have shifts or staffing requirements this week
  const activeRoles = ROLES.filter(role =>
    shifts.some(s => s.role === role)
  );
  // Always show at least the full ROLES list if no shifts yet
  const displayRoles = activeRoles.length > 0 ? activeRoles : ROLES;

  // My shifts only (staff view)
  const myShifts = isOwner ? [] : shifts.filter(s => s.employee_id === user?.employee_id);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="container container-wide">
      <div className="page-header">
        <div>
          <h1 className="page-title">Roster</h1>
          {roster && (
            <p className="page-subtitle">
              Status:{' '}
              <span className={`badge badge-${roster.status === 'published' ? 'success' : roster.status === 'draft' ? 'warning' : 'secondary'}`}>
                {roster.status}
              </span>
            </p>
          )}
        </div>

        <div className="flex gap-2 flex-wrap roster-actions">
          {isOwner && roster && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowVersions(v => !v)}
              title="Version History"
              style={{ display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <History size={14} /> Versions
            </button>
          )}
          {isOwner && roster && roster.status === 'draft' && (
            <button className="btn btn-primary" onClick={handlePublish} disabled={publishing}>
              {publishing ? <span className="spinner" /> : 'Publish Roster'}
            </button>
          )}
          {isOwner && roster && roster.status === 'locked' && (
            <button className="btn btn-secondary btn-sm" onClick={handleUnlock} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Unlock size={14} />Unlock Roster
            </button>
          )}
          {isOwner && (
            <button
              className="btn btn-secondary"
              onClick={handleGenerate}
              disabled={generating || (roster?.status === 'locked')}
            >
              {generating ? <><span className="spinner" /> Generating...</> : 'Generate Roster'}
            </button>
          )}
        </div>
      </div>

      <WeekPicker weekStart={weekStart} onChange={w => { setWeekStart(w); setWarnings([]); }} />

      {error && <div className="alert alert-error mt-3">{error}</div>}

      {/* Warnings panel */}
      {warnings.length > 0 && (
        <div className="card mt-3" style={{ borderLeft: '3px solid var(--color-warning)' }}>
          <div className="card-header">
            <span className="card-title">Generation Warnings ({warnings.length})</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setWarnings([])}>Dismiss</button>
          </div>
          <div className="card-body" style={{ maxHeight: 200, overflowY: 'auto', padding: '8px 16px' }}>
            {warnings.map((w, i) => (
              <div key={i} className={`alert alert-${WARNING_COLORS[w.type] || 'warning'}`} style={{ marginBottom: 4, padding: '6px 10px', fontSize: 12 }}>
                <strong>{w.type.replace(/_/g, ' ')}:</strong> {w.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <span className="spinner spinner-dark" style={{ width: 32, height: 32 }} />
        </div>
      ) : !roster && !isOwner ? (
        <div className="empty-state mt-4">
          <div className="empty-state-icon">📋</div>
          <h3>No roster this week</h3>
          <p>Your manager hasn't generated a roster for this week yet.</p>
        </div>
      ) : (
        <>
          {/* ── Owner: full grid ── */}
          {isOwner && (
            <>
              {/* Desktop: 7-column grid */}
              <div className="roster-grid-wrap mt-3 roster-desktop-only">
                <div className="roster-grid" style={{ gridTemplateColumns: `120px repeat(7, minmax(110px, 1fr))` }}>
                  <div className="roster-grid-corner" />
                  {weekDates.map((date, i) => {
                    const isToday = date === today;
                    return (
                      <div key={date} className={`roster-day-header${isToday ? ' roster-day-today' : ''}`}>
                        <div className="roster-day-name">{DAYS_SHORT[i]}</div>
                        <div className="roster-day-date">
                          {new Date(date + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                        </div>
                      </div>
                    );
                  })}
                  {displayRoles.map(role => (
                    <React.Fragment key={role}>
                      <div className="roster-role-label">{roleLabel(role)}</div>
                      {weekDates.map(date => {
                        const cellShifts = getShiftsFor(date, role);
                        return (
                          <div
                            key={date}
                            className="roster-cell"
                            onClick={() => { if (roster?.status !== 'locked') setModal({ shift: null, date, role }); }}
                          >
                            {cellShifts.map(s => (
                              <div
                                key={s.id}
                                className={`shift-chip${s.is_open ? ' shift-chip-open' : ''}${s.editing_by ? ' shift-chip-editing' : ''}${s.is_locked ? ' shift-chip-locked' : ''}`}
                                onClick={e => { e.stopPropagation(); if (roster?.status !== 'locked') setModal({ shift: s, date, role }); }}
                              >
                                <div className="shift-chip-name">
                                  {s.is_locked && <Lock size={9} style={{ marginRight: 3, verticalAlign: 'middle', flexShrink: 0 }} />}
                                  {s.employee_name || 'Unassigned'}
                                </div>
                                <div className="shift-chip-time">{formatTime(s.start_time)}–{formatTime(s.end_time)}</div>
                                {s.is_open && <div className="shift-chip-badge">OPEN</div>}
                                {s.editing_by && <div className="shift-chip-badge shift-chip-badge-editing">EDITING</div>}
                              </div>
                            ))}
                            {roster?.status !== 'locked' && cellShifts.length === 0 && (
                              <div className="roster-cell-add">+</div>
                            )}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Mobile: day-tab view */}
              <div className="roster-mobile-wrap mt-3">
                {/* Day tabs */}
                <div className="roster-mobile-tabs">
                  {weekDates.map((date, i) => (
                    <button
                      key={date}
                      className={`roster-mobile-tab${mobileDayIdx === i ? ' active' : ''}${date === today ? ' today' : ''}`}
                      onClick={() => setMobileDayIdx(i)}
                    >
                      <span className="roster-mobile-tab-day">{DAYS_SHORT[i]}</span>
                      <span className="roster-mobile-tab-date">
                        {new Date(date + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Selected day's shifts */}
                <div className="roster-mobile-day-panel">
                  {displayRoles.map(role => {
                    const cellShifts = getShiftsFor(weekDates[mobileDayIdx], role);
                    return (
                      <div key={role} className="roster-mobile-role-row"
                        onClick={() => { if (roster?.status !== 'locked') setModal({ shift: null, date: weekDates[mobileDayIdx], role }); }}
                      >
                        <div className="roster-mobile-role-label">{roleLabel(role)}</div>
                        <div className="roster-mobile-chips">
                          {cellShifts.length === 0 ? (
                            roster?.status !== 'locked'
                              ? <span className="roster-mobile-add-btn">＋ Add shift</span>
                              : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                          ) : cellShifts.map(s => (
                            <div
                              key={s.id}
                              className={`shift-chip${s.is_open ? ' shift-chip-open' : ''}${s.is_locked ? ' shift-chip-locked' : ''}`}
                              style={{ fontSize: 12 }}
                              onClick={e => { e.stopPropagation(); if (roster?.status !== 'locked') setModal({ shift: s, date: weekDates[mobileDayIdx], role }); }}
                            >
                              <div className="shift-chip-name">
                                {s.is_locked && <Lock size={9} style={{ marginRight: 3, verticalAlign: 'middle', flexShrink: 0 }} />}
                                {s.employee_name || 'Unassigned'}
                              </div>
                              <div className="shift-chip-time">{formatTime(s.start_time)}–{formatTime(s.end_time)}</div>
                              {s.is_open && <div className="shift-chip-badge">OPEN</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* ── Staff: own shifts only ── */}
          {!isOwner && (
            <div className="mt-3">
              {myShifts.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📅</div>
                  <h3>No shifts this week</h3>
                  <p>You have no scheduled shifts. Check open shifts if you'd like to pick one up.</p>
                </div>
              ) : (
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">My Shifts This Week</span>
                    <span className="text-muted text-sm">{myShifts.length} shift{myShifts.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ padding: '12px 16px', overflow: 'hidden' }}>
                  <div className="table-wrap table-wrap-nostack" style={{ borderRadius: 0, border: 'none', margin: '0 -16px' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Day</th>
                          <th>Date</th>
                          <th>Role</th>
                          <th>Start</th>
                          <th>End</th>
                          <th>Hours</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myShifts
                          .sort((a, b) => a.date.localeCompare(b.date))
                          .map(s => {
                            const [sh, sm] = s.start_time.split(':').map(Number);
                            const [eh, em] = s.end_time.split(':').map(Number);
                            const hours = ((eh * 60 + em) - (sh * 60 + sm)) / 60;
                            return (
                              <tr key={s.id}>
                                <td data-label="Day">{new Date(s.date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short' })}</td>
                                <td data-label="Date">{new Date(s.date + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</td>
                                <td data-label="Role"><span className="badge badge-primary">{roleLabel(s.role)}</span></td>
                                <td data-label="Start">{formatTime(s.start_time)}</td>
                                <td data-label="End">{formatTime(s.end_time)}</td>
                                <td data-label="Hours">{hours.toFixed(1)}h</td>
                                <td data-label="Status">
                                  <span className={`badge badge-${s.status === 'scheduled' ? 'success' : s.status === 'open' ? 'danger' : 'secondary'}`}>
                                    {s.status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                  </div>
                </div>
              )}

              {/* All published shifts for staff visibility */}
              {(roster?.status === 'published' || roster?.status === 'locked') && shifts.length > myShifts.length && (
                <div className="card mt-3">
                  <div className="card-header">
                    <span className="card-title">Full Week Roster</span>
                    <span className={`badge ${roster?.status === 'locked' ? 'badge-secondary' : 'badge-success'}`}>
                      {roster?.status === 'locked' ? 'Locked' : 'Published'}
                    </span>
                  </div>
                  <div className="roster-grid-wrap">
                    <div className="roster-grid" style={{ gridTemplateColumns: `120px repeat(7, minmax(110px, 1fr))` }}>
                      <div className="roster-grid-corner" />
                      {weekDates.map((date, i) => {
                        const isToday = date === today;
                        return (
                          <div key={date} className={`roster-day-header${isToday ? ' roster-day-today' : ''}`}>
                            <div className="roster-day-name">{DAYS_SHORT[i]}</div>
                            <div className="roster-day-date">
                              {new Date(date + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                            </div>
                          </div>
                        );
                      })}
                      {displayRoles.filter(r => shifts.some(s => s.role === r)).map(role => (
                        <React.Fragment key={role}>
                          <div className="roster-role-label">{roleLabel(role)}</div>
                          {weekDates.map(date => {
                            const cellShifts = getShiftsFor(date, role);
                            return (
                              <div key={date} className="roster-cell roster-cell-readonly">
                                {cellShifts.map(s => (
                                  <div
                                    key={s.id}
                                    className={`shift-chip${s.employee_id === user?.employee_id ? ' shift-chip-mine' : ''}${s.is_open ? ' shift-chip-open' : ''}`}
                                    onClick={s.is_open && !requestDone?.includes?.(s.id) && s.role === user?.employeeRole ? () => setOpenShiftModal({ shift: s }) : undefined}
                                    style={s.is_open && s.role === user?.employeeRole ? { cursor: 'pointer', outline: '2px solid #f97316', outlineOffset: 1 } : undefined}
                                  >
                                    <div className="shift-chip-name">
                                      {s.is_open ? '🟠 OPEN' : (s.employee_name || 'Unassigned')}
                                    </div>
                                    <div className="shift-chip-time">{formatTime(s.start_time)}–{formatTime(s.end_time)}</div>
                                    {s.is_open && s.role === user?.employeeRole && <div style={{ fontSize: 9, fontWeight: 700, color: '#f97316' }}>tap to request</div>}
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No roster yet – owner prompt */}
          {isOwner && !roster && (
            <div className="empty-state mt-4">
              <div className="empty-state-icon">📋</div>
              <h3>No roster for this week</h3>
              <p>Click "Generate Roster" to automatically create a roster based on staff availability and your staffing rules.</p>
              <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
                {generating ? 'Generating...' : 'Generate Roster'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Shift edit/add modal */}
      {modal && isOwner && (
        <ShiftModal
          shift={modal.shift}
          date={modal.date}
          role={modal.role}
          employees={employees}
          rosterId={roster?.id}
          onClose={() => setModal(null)}
          onSaved={handleShiftSaved}
          onDeleted={handleShiftDeleted}
          roleNames={roleNames}
        />
      )}

      {/* Open shift request modal (staff) */}
      {openShiftModal && !isOwner && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setOpenShiftModal(null)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 360 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>Open Shift Available</div>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 16 }}>
              {new Date(openShiftModal.shift.date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' })}
            </div>
            <div className="card" style={{ padding: '12px 16px', marginBottom: 16, background: 'var(--bg-subtle)' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{roleLabel(openShiftModal.shift.role)}</div>
              <div style={{ fontSize: 15 }}>{formatTime(openShiftModal.shift.start_time)} – {formatTime(openShiftModal.shift.end_time)}</div>
            </div>
            {myShifts.some(s => s.date === openShiftModal.shift.date) && (
              <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 6, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
                ⚠️ You already have a shift on this day. Check for conflicts.
              </div>
            )}
            {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }}
                onClick={() => handleRequestOpenShift(openShiftModal.shift.id)}
                disabled={requesting}>
                {requesting ? <span className="spinner" /> : 'Request This Shift'}
              </button>
              <button className="btn btn-secondary" onClick={() => setOpenShiftModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Version history panel */}
      {showVersions && roster && isOwner && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 199, background: 'rgba(0,0,0,0.2)' }}
            onClick={() => setShowVersions(false)}
          />
          <RosterVersionPanel
            roster={roster}
            onClose={() => setShowVersions(false)}
            onRestored={(restoredShifts) => {
              setShifts(restoredShifts);
              setRoster(prev => ({ ...prev, status: 'draft', published_at: null }));
              setShowVersions(false);
            }}
          />
        </>
      )}
    </div>
  );
}
